/**
 * Product tabs.
 *
 * The server sends a stack of `details` elements, one per tab block. This file
 * upgrades that stack into a tablist when there is room for one, and otherwise
 * leaves it as the accordion it already is. Doing it in that direction rather
 * than the other means a blocked or failed script degrades to working markup:
 * `details` opens and closes on its own, with no JavaScript involved.
 *
 * The two modes are genuinely different widgets, not one widget with a
 * stylesheet swap, so ARIA is rewritten on every switch. In tabs mode the
 * `details` are forced open and hidden or shown as whole panels; in accordion
 * mode every attribute the tabs mode added is removed and the browser's own
 * behaviour takes back over.
 *
 * <product-tabs> re-initialises for free after a theme-editor reload: replaced
 * markup upgrades into a new element instance and every listener lives on the
 * element, so nothing accumulates.
 */
(function () {
  // Below this the tab bar would either wrap onto several lines or scroll the
  // later tabs out of reach, so "responsive" falls back to the accordion here.
  const WIDE = window.matchMedia('(min-width: 750px)');

  class ProductTabs extends HTMLElement {
    connectedCallback() {
      // Moving an element in the DOM disconnects and reconnects it. The markup is
      // already upgraded at that point, so only the listeners that were dropped on
      // disconnect are put back — building the bar again would double every tab.
      if (this.initialised) {
        this.listen();
        return;
      }
      this.initialised = true;

      this.list = this.querySelector('[data-pdp-tabs-list]');
      this.panels = this.querySelector('[data-pdp-tabs-panels]');
      if (!this.list || !this.panels) return;

      // Scoped to this element's own panels: a nested app block could contain a
      // `details` of its own, and only direct tab blocks may become tabs.
      this.tabs = Array.from(this.panels.querySelectorAll('[data-pdp-tab]')).filter(
        (tab) => tab.closest('[data-pdp-tabs]') === this
      );
      if (!this.tabs.length) return;

      this.buttons = [];
      this.active = 0;
      this.buildButtons();

      this.onListClick = this.onListClick.bind(this);
      this.onListKeydown = this.onListKeydown.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onHashChange = this.onHashChange.bind(this);

      this.list.addEventListener('click', this.onListClick);
      this.list.addEventListener('keydown', this.onListKeydown);
      this.listen();

      this.apply();
      this.revealFromHash(false);
    }

    /** The two listeners that live outside this element, so they leak if left on. */
    listen() {
      WIDE.addEventListener('change', this.onViewportChange);
      window.addEventListener('hashchange', this.onHashChange);
    }

    disconnectedCallback() {
      WIDE.removeEventListener('change', this.onViewportChange);
      window.removeEventListener('hashchange', this.onHashChange);
    }

    /** Tabs need room; the merchant can also pin either mode. */
    get mode() {
      const display = this.dataset.display;
      if (display === 'accordion') return 'accordion';
      if (display === 'tabs') return 'tabs';
      return WIDE.matches ? 'tabs' : 'accordion';
    }

    /** One tab is a description, not a tabbed interface — if the merchant agrees. */
    get barless() {
      return this.tabs.length < 2 && this.hasAttribute('data-hide-single');
    }

    buildButtons() {
      const frag = document.createDocumentFragment();

      this.tabs.forEach((tab, i) => {
        const summary = tab.querySelector('[data-pdp-tab-summary]');
        const panel = tab.querySelector('[data-pdp-tab-panel]');
        if (!panel) return;

        // Ids have to survive a section re-render, and a block id is stable
        // where a counter is not.
        const key = `${this.dataset.sectionId || 'tabs'}-${i}`;
        panel.id = panel.id || `pdp-tab-panel-${key}`;

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pdp-tabs__button';
        button.id = `pdp-tab-button-${key}`;
        button.dataset.index = String(i);
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panel.id);

        // The label is whatever the summary shows, icon included, so the tab bar
        // and the accordion never drift apart. The marker is the accordion's
        // chevron and has no business in a tab.
        if (summary) {
          Array.from(summary.childNodes).forEach((node) => {
            if (node.nodeType === 1 && node.classList.contains('pdp-tab__marker')) return;
            button.appendChild(node.cloneNode(true));
          });
        } else {
          button.textContent = tab.dataset.tabTitle || '';
        }

        this.buttons.push(button);
        frag.appendChild(button);
      });

      this.list.appendChild(frag);
    }

    apply() {
      const mode = this.mode;
      const barless = this.barless;
      this.dataset.mode = barless ? 'plain' : mode;

      this.list.hidden = barless || mode !== 'tabs';

      if (barless) {
        this.tabs.forEach((tab) => this.toAccordion(tab, true));
        return;
      }

      if (mode === 'tabs') {
        // Nothing was selected yet, or the previously open accordion row is the
        // sensible tab to land on.
        const opened = this.tabs.findIndex((tab) => tab.open);
        this.select(opened > -1 ? opened : this.active, false);
      } else {
        this.tabs.forEach((tab, i) => {
          const open = this.hasAttribute('data-open-first') ? i === this.active : tab.open;
          this.toAccordion(tab, open);
        });
      }
    }

    /** Hands the row back to the browser: no roles, no forced state. */
    toAccordion(tab, open) {
      const panel = tab.querySelector('[data-pdp-tab-panel]');
      const summary = tab.querySelector('[data-pdp-tab-summary]');
      tab.hidden = false;
      tab.open = open;
      if (panel) {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
        panel.removeAttribute('tabindex');
      }
      if (summary) summary.removeAttribute('aria-hidden');
    }

    select(index, focus) {
      const count = this.tabs.length;
      if (!count) return;
      this.active = ((index % count) + count) % count;

      this.tabs.forEach((tab, i) => {
        const on = i === this.active;
        const panel = tab.querySelector('[data-pdp-tab-panel]');
        const summary = tab.querySelector('[data-pdp-tab-summary]');

        // Forced open so the panel is laid out; visibility is the `hidden` on the
        // `details` itself, which keeps the closed panels out of the a11y tree.
        tab.open = true;
        tab.hidden = !on;

        if (panel) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', this.buttons[i] ? this.buttons[i].id : '');
          // Focusable so a keyboard user can reach a panel whose content has no
          // focusable element of its own and still scroll it.
          panel.setAttribute('tabindex', '0');
        }
        // The summary duplicates the tab button, and two labels for one panel
        // read as two controls.
        if (summary) summary.setAttribute('aria-hidden', 'true');
      });

      this.buttons.forEach((button, i) => {
        const on = i === this.active;
        button.setAttribute('aria-selected', on ? 'true' : 'false');
        // Roving tabindex: one stop for the whole bar, arrows move within it.
        button.tabIndex = on ? 0 : -1;
        if (on && focus) button.focus();
      });
    }

    onListClick(event) {
      const button = event.target.closest('.pdp-tabs__button');
      if (!button || !this.list.contains(button)) return;
      this.select(Number(button.dataset.index), false);
    }

    onListKeydown(event) {
      const keys = {
        ArrowRight: 1,
        ArrowLeft: -1,
        ArrowDown: 1,
        ArrowUp: -1
      };

      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.select(event.key === 'Home' ? 0 : this.tabs.length - 1, true);
        return;
      }

      const step = keys[event.key];
      if (!step) return;
      event.preventDefault();
      // Right/left follow the writing direction; up/down never do.
      const rtl = getComputedStyle(this).direction === 'rtl';
      const horizontal = event.key === 'ArrowRight' || event.key === 'ArrowLeft';
      this.select(this.active + (rtl && horizontal ? -step : step), true);
    }

    onViewportChange() {
      if (this.dataset.display === 'responsive') this.apply();
    }

    onHashChange() {
      this.revealFromHash(true);
    }

    /**
     * A link to `#judgeme_product_reviews`, or to any other anchor an app drops
     * inside a panel, is useless if the panel it lives in is the closed one.
     */
    revealFromHash(scroll) {
      const id = window.location.hash.slice(1);
      if (!id) return;

      let target = null;
      try {
        target = this.querySelector(`#${CSS.escape(id)}`);
      } catch (error) {
        return;
      }
      if (!target) return;

      const tab = target.closest('[data-pdp-tab]');
      const index = this.tabs.indexOf(tab);
      if (index < 0) return;

      if (this.mode === 'tabs' && !this.barless) this.select(index, false);
      else tab.open = true;

      if (scroll) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  if (!customElements.get('product-tabs')) {
    customElements.define('product-tabs', ProductTabs);
  }

  // The theme editor selects a block to show the merchant what they just edited.
  // In tabs mode that block may be inside a panel nobody can see.
  document.addEventListener('shopify:block:select', (event) => {
    const host = event.target.closest('[data-pdp-tabs]');
    if (!host || typeof host.select !== 'function') return;

    const tab = event.target.closest('[data-pdp-tab]') || event.target.querySelector('[data-pdp-tab]');
    const index = host.tabs ? host.tabs.indexOf(tab) : -1;
    if (index < 0) return;

    if (host.mode === 'tabs' && !host.barless) host.select(index, false);
    else tab.open = true;
  });
})();
